import { G, UI, resetGame } from "./store";
import type { C2S, S2C, Col } from "../shared/types";

let ws: WebSocket | null = null;
let gameId = "";
let retry = 0;
let closedByUs = false;
let pingTimer: any;
const pendingAcks = new Map<string, (id: string) => void>();
type FxL = (fx: any, from: string) => void;
const fxListeners = new Set<FxL>();
type RtcL = (from: string, data: any) => void;
const rtcListeners = new Set<RtcL>();
type ChatL = (msg: any) => void;
const chatListeners = new Set<ChatL>();

export const onFx = (f: FxL) => { fxListeners.add(f); return () => { fxListeners.delete(f); }; };
export const onRtc = (f: RtcL) => { rtcListeners.add(f); return () => { rtcListeners.delete(f); }; };
export const onChat = (f: ChatL) => { chatListeners.add(f); return () => { chatListeners.delete(f); }; };

// ids the local user is actively dragging: ignore server echoes for them
export const localLocks = new Set<string>();

export function connect(id: string) {
  disconnect();
  gameId = id;
  closedByUs = false;
  retry = 0;
  open();
}

export function disconnect() {
  closedByUs = true;
  clearInterval(pingTimer);
  ws?.close();
  ws = null;
  resetGame();
}

function open() {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const sock = new WebSocket(`${proto}://${location.host}/ws?game=${encodeURIComponent(gameId)}`);
  ws = sock;
  sock.onopen = () => {
    retry = 0;
    clearInterval(pingTimer);
    pingTimer = setInterval(() => send({ t: "ping" }), 25000);
  };
  sock.onmessage = ev => {
    let m: S2C;
    try { m = JSON.parse(ev.data); } catch { return; }
    handle(m);
  };
  sock.onclose = ev => {
    if (ws !== sock) return;
    G.set({ connected: false });
    clearInterval(pingTimer);
    if (closedByUs) return;
    if (ev.code === 4003 || ev.code === 4001 || ev.code === 4004) {
      G.set({ error: ev.code === 4003 ? "You were removed from this game." : "This game no longer exists." });
      return;
    }
    const delay = Math.min(10000, 500 * 2 ** retry++);
    setTimeout(() => { if (!closedByUs && ws === sock) open(); }, delay);
  };
}

function handle(m: S2C) {
  const s = G.get();
  switch (m.t) {
    case "snap": {
      const recs: any = {};
      for (const [col, list] of Object.entries(m.recs)) {
        recs[col] = {};
        for (const r of list) recs[col][r.id] = r;
      }
      G.set({ connected: true, me: m.me, game: m.game, members: m.members, online: m.online, recs, chat: m.chat, chatMore: m.chat.length >= 150, error: null });
      // drop selections / windows that no longer exist
      const ui = UI.get();
      UI.set({ selected: ui.selected.filter(id => recs.obj[id]) });
      break;
    }
    case "put": {
      if (m.col === "obj" && localLocks.has(m.rec.id)) {
        // keep our in-flight position but take everything else
        const cur = s.recs.obj[m.rec.id];
        if (cur) m.rec = { ...m.rec, x: cur.x, y: cur.y, w: cur.w, h: cur.h, rot: cur.rot, pts: cur.pts };
      }
      G.set({ recs: { ...s.recs, [m.col]: { ...s.recs[m.col], [m.rec.id]: m.rec } } });
      break;
    }
    case "del": {
      const c = { ...s.recs[m.col] };
      delete (c as any)[m.id];
      G.set({ recs: { ...s.recs, [m.col]: c } });
      if (m.col === "obj") {
        const sel = UI.get().selected;
        if (sel.includes(m.id)) UI.set({ selected: sel.filter(x => x !== m.id) });
      }
      break;
    }
    case "game":
      G.set({ game: m.game });
      break;
    case "members":
      G.set({ members: m.members });
      break;
    case "presence":
      G.set({ online: m.online });
      break;
    case "chat":
      G.set({ chat: [...s.chat, m.msg].slice(-1000) });
      chatListeners.forEach(f => f(m.msg));
      break;
    case "chatMore":
      G.set({ chat: [...m.msgs, ...s.chat], chatMore: m.msgs.length >= 100 });
      break;
    case "fx":
      fxListeners.forEach(f => f(m.fx, m.from));
      break;
    case "rtc":
      rtcListeners.forEach(f => f(m.from, m.data));
      break;
    case "ack": {
      const cb = pendingAcks.get(m.rid);
      pendingAcks.delete(m.rid);
      cb?.(m.id);
      break;
    }
    case "err":
      toast(m.msg);
      break;
    case "kicked":
      G.set({ error: "You were removed from this game." });
      break;
  }
}

export function send(m: C2S) {
  if (ws && ws.readyState === 1) ws.send(JSON.stringify(m));
}

export function create(col: Col, data: any): Promise<string> {
  const rid = Math.random().toString(36).slice(2);
  return new Promise(res => {
    pendingAcks.set(rid, res);
    send({ t: "create", col, data, rid });
  });
}

/** Optimistic local update + send. */
export function update(col: Col, id: string, patch: any, local = true) {
  const s = G.get();
  const cur = (s.recs as any)[col][id];
  if (!cur) return;
  if (local) {
    const next = { ...cur };
    for (const [k, v] of Object.entries(patch)) {
      if (col === "char" && k === "attrs") {
        const a = { ...(cur.attrs || {}) };
        for (const [ak, av] of Object.entries(v as any)) av === null ? delete a[ak] : (a[ak] = String(av));
        next.attrs = a;
      } else if (col === "char" && k === "rep") next.rep = { ...(cur.rep || {}), ...(v as any) };
      else if (v === null) delete next[k];
      else next[k] = v;
    }
    G.set({ recs: { ...s.recs, [col]: { ...(s.recs as any)[col], [id]: next } } });
  }
  send({ t: "update", col, id, patch });
}

export function remove(col: Col, id: string) {
  send({ t: "delete", col, id });
}

export function setGame(patch: any) {
  const g = G.get().game;
  if (g) {
    const { settings, name, ...st } = patch;
    G.set({ game: { ...g, name: name ?? g.name, settings: { ...g.settings, ...(settings || {}) }, state: { ...g.state, ...st } } });
  }
  send({ t: "game", patch });
}

// ---- toasts ----
type ToastL = (msg: string) => void;
const toastL = new Set<ToastL>();
export const onToast = (f: ToastL) => { toastL.add(f); return () => { toastL.delete(f); }; };
export function toast(msg: string) { toastL.forEach(f => f(msg)); }
